import {
  AvatarGroup,
  Badge,
  Box,
  Flex,
  HStack,
  Input,
  Popover,
  Portal,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { useSearchAffiliateUsers } from '@/hooks/queries/use-search-affiliate-users';

interface AffiliateFilterProps {
  paramPrefix?: string;
  paramName?: string;
}

type AffiliateLite = {
  name: string;
  avatarUrl: string | null;
};

export const AffiliateFilter: React.FC<AffiliateFilterProps> = ({
  paramPrefix = '',
  paramName,
}) => {
  const { t } = useTranslation();
  const affiliateIdsParam =
    paramName ?? (paramPrefix ? `${paramPrefix}AffiliateIds` : 'affiliateIds');
  const [urlAffiliateIds, setUrlAffiliateIds] = useQueryState(affiliateIdsParam, parseAsString);
  const [search, setSearch] = useState('');

  const selectedAffiliateIds = useMemo(
    () => (urlAffiliateIds ? urlAffiliateIds.split(',').filter(Boolean) : []),
    [urlAffiliateIds]
  );

  const { data: affiliatesResponse, isLoading } = useSearchAffiliateUsers(search, {
    enabledWhenEmpty: true,
    limit: 50,
    status: 'all',
  });

  const affiliates = useMemo(() => affiliatesResponse?.data ?? [], [affiliatesResponse]);
  const [tempAffiliateIds, setTempAffiliateIds] = useState<string[]>(selectedAffiliateIds);
  // Cache avatars for currently-selected affiliates so we can render them when they
  // aren't in the current search page.
  const [knownAffiliates, setKnownAffiliates] = useState<Record<string, AffiliateLite>>({});

  useEffect(() => {
    setTempAffiliateIds(selectedAffiliateIds);
  }, [selectedAffiliateIds]);

  useEffect(() => {
    if (affiliates.length === 0) return;
    setKnownAffiliates((prev) => {
      const additions = affiliates.filter(
        (affiliate) => !prev[affiliate.id] && selectedAffiliateIds.includes(affiliate.id)
      );
      if (additions.length === 0) return prev;
      return {
        ...prev,
        ...Object.fromEntries(
          additions.map((affiliate) => [
            affiliate.id,
            { name: affiliate.name, avatarUrl: affiliate.avatar?.fileUrl ?? null },
          ])
        ),
      };
    });
  }, [affiliates, selectedAffiliateIds]);

  const handleApply = useCallback(() => {
    setUrlAffiliateIds(tempAffiliateIds.length > 0 ? tempAffiliateIds.join(',') : null);
  }, [tempAffiliateIds, setUrlAffiliateIds]);

  const handleClear = useCallback(() => {
    setTempAffiliateIds([]);
    setUrlAffiliateIds(null);
  }, [setUrlAffiliateIds]);

  const handleClose = useCallback(() => {
    setTempAffiliateIds(selectedAffiliateIds);
    setSearch('');
  }, [selectedAffiliateIds]);

  const handleAffiliateSelectionToggle = (affiliateId: string) => {
    setTempAffiliateIds((prevIds) =>
      prevIds.includes(affiliateId)
        ? prevIds.filter((id) => id !== affiliateId)
        : [...prevIds, affiliateId]
    );
  };

  const isFilterActive = selectedAffiliateIds.length > 0;

  const renderActiveOptions = () => (
    <Flex align="center" gap={2}>
      <AvatarGroup gap="0" spaceX="-3" size="2xs">
        {selectedAffiliateIds.slice(0, 3).map((id) => {
          const affiliate = knownAffiliates[id];
          return (
            <Avatar
              key={id}
              size="2xs"
              src={affiliate?.avatarUrl ?? undefined}
              name={affiliate?.name || t('affiliates.filters.selectedAffiliateFallback')}
            />
          );
        })}
      </AvatarGroup>
      {selectedAffiliateIds.length > 3 && `+${selectedAffiliateIds.length - 3}`}
    </Flex>
  );

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear} onClose={handleClose}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('affiliates.filters.affiliate')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>{renderActiveOptions()}</FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="320px">
            <FilterButtonMenuHeader
              title={t('affiliates.filters.affiliate')}
              onClearAll={handleClear}
            />
            <Box p={3}>
              <Input
                variant="outline"
                size="sm"
                mb={2}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('affiliates.filters.search')}
              />
              {isLoading ? (
                <Box>
                  <Skeleton height="34px" mb={1} />
                  <Skeleton height="34px" mb={1} />
                  <Skeleton height="34px" />
                </Box>
              ) : (
                <VStack gap={0.5} align="stretch" maxHeight="300px" overflowY="auto" pr={1}>
                  {affiliates.length === 0 && (
                    <Text fontSize="sm" color="dark" textAlign="center" py={2}>
                      {t('affiliates.filters.noAffiliatesAvailable')}
                    </Text>
                  )}
                  {affiliates.map((affiliate) => {
                    const selected = tempAffiliateIds.includes(affiliate.id);
                    return (
                      <FilterMenuItem
                        key={affiliate.id}
                        onClick={() => handleAffiliateSelectionToggle(affiliate.id)}
                        isSelected={selected}
                      >
                        <Flex align="center" gap={2.5} minW={0} flex="1">
                          <Checkbox checked={selected} pointerEvents="none" aria-hidden size="sm" />
                          <Flex align="center" gap={2} minW={0} flex="1">
                            <Avatar
                              size="xs"
                              src={affiliate.avatar?.fileUrl ?? undefined}
                              name={affiliate.name}
                            />
                            <VStack align="flex-start" gap={1} minW={0} flex="1">
                              <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                                {affiliate.name}
                              </Text>
                              {affiliate.programs[0]?.name && (
                                <HStack gap={1} minW={0} maxW="100%">
                                  <Badge size="xs" colorPalette="gray" variant="subtle" maxW="100%">
                                    <Text as="span" lineClamp={1}>
                                      {affiliate.programs[0].name}
                                    </Text>
                                  </Badge>
                                </HStack>
                              )}
                            </VStack>
                          </Flex>
                        </Flex>
                      </FilterMenuItem>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
